/* Botanical Contact — pressed-flower greeting card. Soft sage,
   rounded inputs, leaf flourishes around the form. */

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Leaf, Mail, MapPin, Instagram } from "lucide-react";
import { api } from "@/lib/api";
import { useSiteSettings } from "@/hooks/useSiteSettings";

export default function BotanicalContact() {
  const { settings } = useSiteSettings();
  const studioName = settings?.studio_name || "Studio";
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  useEffect(() => { document.title = `${studioName} — Send a Note`; }, [studioName]);

  async function onSubmit(e) {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Add your name, email, and a short note."); return; }
    setSending(true);
    try {
      await api.post("/contact", form);
      setForm({ name: "", email: "", phone: "", message: "" });
      toast.success("❀ Note received. We will write back within a few days.");
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not deliver — please try again."); }
    finally { setSending(false); }
  }

  return (
    <div data-testid="botanical-contact" style={{ background: "var(--tenant-bg, #F6F4EE)", color: "var(--tenant-fg, #2A3A20)" }}>
      <section className="px-6 lg:px-12 pt-16 pb-12 text-center">
        <div className="inline-flex items-center gap-3 mb-5">
          <Leaf className="w-5 h-5" strokeWidth={1.25} style={{ color: "var(--tenant-accent)" }} />
          <div className="text-[10px] uppercase tracking-[0.42em]" style={{ color: "var(--tenant-accent)" }}>
            Send a Note
          </div>
          <Leaf className="w-5 h-5 -scale-x-100" strokeWidth={1.25} style={{ color: "var(--tenant-accent)" }} />
        </div>
        <h1 style={{ fontFamily: "var(--tenant-serif)", fontSize: "clamp(2.5rem, 5.5vw, 4.5rem)" }}>
          <em className="italic">Reach into the garden.</em>
        </h1>
      </section>

      <div className="max-w-2xl mx-auto px-6 pb-32">
        <div className="p-8 lg:p-10 rounded-[36px]" style={{ background: "color-mix(in srgb, var(--tenant-accent) 12%, var(--tenant-bg))" }}>
          <form onSubmit={onSubmit} className="space-y-6">
            {[
              { name: "name", label: "Your name", type: "text" },
              { name: "email", label: "Email", type: "email" },
              { name: "phone", label: "Phone (optional)", type: "tel" },
            ].map((f) => (
              <div key={f.name}>
                <label className="block text-[10px] uppercase tracking-[0.32em] mb-2" style={{ color: "var(--tenant-accent)" }}>
                  ❀ {f.label}
                </label>
                <input type={f.type} value={form[f.name]}
                  onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                  data-testid={`botanical-contact-${f.name}`}
                  className="w-full px-4 py-3 rounded-2xl outline-none border focus:border-current"
                  style={{ background: "var(--tenant-bg)", borderColor: "color-mix(in srgb, var(--tenant-accent) 30%, transparent)", fontFamily: "var(--tenant-serif)" }} />
              </div>
            ))}
            <div>
              <label className="block text-[10px] uppercase tracking-[0.32em] mb-2" style={{ color: "var(--tenant-accent)" }}>
                ❀ Your note
              </label>
              <textarea rows={6} value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                data-testid="botanical-contact-message"
                className="w-full px-4 py-3 rounded-2xl outline-none border focus:border-current resize-none"
                style={{ background: "var(--tenant-bg)", borderColor: "color-mix(in srgb, var(--tenant-accent) 30%, transparent)", fontFamily: "var(--tenant-serif)" }} />
            </div>
            <div className="text-center pt-2">
              <button type="submit" disabled={sending} data-testid="botanical-contact-submit"
                className="inline-flex items-center gap-3 px-8 py-3.5 text-[11px] uppercase tracking-[0.32em] rounded-full disabled:opacity-60"
                style={{ background: "var(--tenant-accent)", color: "var(--tenant-bg)" }}>
                {sending ? "Sending..." : "Press & send →"}
              </button>
            </div>
          </form>
        </div>

        {(settings?.contact_email || settings?.contact_location || settings?.instagram_url) && (
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6 text-center text-sm" style={{ color: "var(--tenant-fg-soft)" }}>
            {settings?.contact_location && (
              <div className="flex flex-col items-center gap-2">
                <MapPin className="w-4 h-4" strokeWidth={1.25} style={{ color: "var(--tenant-accent)" }} />
                <span>{settings.contact_location}</span>
              </div>
            )}
            {settings?.contact_email && (
              <a href={`mailto:${settings.contact_email}`} className="flex flex-col items-center gap-2 hover:underline">
                <Mail className="w-4 h-4" strokeWidth={1.25} style={{ color: "var(--tenant-accent)" }} />
                <span>{settings.contact_email}</span>
              </a>
            )}
            {settings?.instagram_url && (
              <a href={settings.instagram_url} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-2 hover:underline">
                <Instagram className="w-4 h-4" strokeWidth={1.25} style={{ color: "var(--tenant-accent)" }} />
                <span>Follow the garden</span>
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
